import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBreadcrumb from "@/components/base/PageBreadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getNewsById } from "@/lib/media";
import { newsList } from "@/mocks/news";

export default function NewsDetailPage() {
  const { id = "" } = useParams();
  const article = getNewsById(id);

  usePageMeta(
    article ? `${article.title} - 影视资讯 - 欧美日韩影视平台` : "影视资讯 - 欧美日韩影视平台",
    article ? article.summary : "影视资讯详情页面",
    article ? article.tags.join(",") : "影视资讯",
  );

  if (!article) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-5">
            <i className="ri-article-line text-4xl text-foreground-400" />
          </span>
          <h1 className="text-xl font-bold text-foreground-900 font-heading">未找到该资讯</h1>
          <p className="mt-2 text-sm text-foreground-500">文章可能已被移除，返回资讯列表继续浏览。</p>
          <Link
            to="/category/news"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            返回资讯列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const moreNews = newsList.filter((n) => n.id !== article.id).slice(0, 4);

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 pt-6">
        <PageBreadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "影视资讯", to: "/category/news" },
            { label: article.category },
          ]}
        />

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <article className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-accent-500 text-background-50">
                {article.category}
              </span>
              <span className="text-xs text-foreground-500">{article.date}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-foreground-950 font-heading leading-tight">
              {article.title}
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed border-l-2 border-accent-400 pl-4">
              {article.summary}
            </p>

            <div className="mt-6 w-full h-[240px] md:h-[380px] rounded-lg overflow-hidden bg-background-200">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} 影视资讯`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-6 space-y-4">
              {article.content.map((p, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose">
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {article.tags.map((t) => (
                <span
                  key={t}
                  className="text-xs px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900"
                >
                  #{t}
                </span>
              ))}
            </div>
          </article>

          <aside className="space-y-5">
            <div className="bg-background-50 border border-background-200 rounded-lg p-5">
              <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950 font-heading mb-4">
                <i className="ri-newspaper-line text-primary-500" />
                相关资讯
              </h3>
              <ul className="space-y-4">
                {moreNews.map((n) => (
                  <li key={n.id}>
                    <Link to={`/news/${n.id}`} className="flex gap-3 group">
                      <div className="w-20 h-14 rounded-md overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={n.cover}
                          alt={n.title}
                          title={`${n.title} 影视资讯`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {n.title}
                        </p>
                        <span className="text-[11px] text-foreground-400 mt-1 inline-block">
                          {n.date}
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <Link
              to="/category/news"
              className="flex items-center justify-center gap-2 py-3 rounded-md bg-primary-50 border border-primary-100 text-sm font-medium text-primary-700 hover:bg-primary-100 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              返回资讯列表
            </Link>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}